import { BULK_FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import { useSearchFilterContext } from '@aibees/elephant/feature/search-filters/context';
import {
  useSearchQueryFilter,
  useSearchTypeFromRoute,
  useTrackEvents,
} from '@aibees/elephant/hooks';
import { useCreateSearchList } from '@aibees/elephant/hooks/search-list/use-create-search-list';
import { useReadSearchList } from '@aibees/elephant/hooks/search-list/use-read-search-list';
import {
  FormDialog,
  FormDialogSubmitHandler,
  Icon,
} from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Alert, Box, Button, Divider, Typography } from '@mui/material';
import { Fragment, memo, useMemo, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { createConfig } from '../../config';
import { useBulkFilterValuesCount } from '../../hooks/use-bulk-filter-values-count';
import { BulkInputFilterValidationResult } from '../../types';
import { buildBulkInputSearchParamsDraft } from '../../utils';
import { BulkFilterInputError } from '../bulk-filter-input-error';
import { BulkFilterValidationsDialog } from '../bulk-filter-validations-dialog';
import { BulkInputFilterTextarea } from '../bulk-input-filter-textarea';
import { BulkInputFilterViewButton } from '../bulk-input-filter-view-button';
import * as SX from './sx';
import type { BulkInputFilterItemProps as Props } from './types';

// Manual (Include/Exclude) search filter keys used to detect whether any
// company-scoped filter outside the current bulk item is active.
const MANUAL_SEARCH_FILTER_KEYS = [
  'include_current_company',
  'exclude_current_company',
  'include_company_ids',
  'exclude_company_ids',
  'bulk_include_company_social',
  'bulk_include_company_domain',
  'bulk_include_company_name',
  'bulk_exclude_company_social',
  'bulk_exclude_company_domain',
  'bulk_exclude_company_name',
];

export const BulkInputFilterItem = memo(({ className, item }: Props) => {
  const fields = useMemo(
    () => item.items?.map((subItem) => subItem.field!) ?? [],
    [item.items]
  );
  const { setParams, params } = useSearchQueryFilter({ select: fields });
  // Subscribe separately to the manual (Include/Exclude) search filter keys.
  // The `params` above is scoped to `fields`, so it can't be used to detect
  // filters that live outside this bulk item.
  const { params: manualFilterParams } = useSearchQueryFilter({
    select: MANUAL_SEARCH_FILTER_KEYS,
  });
  const [invalidItemsCount, setInvalidItemsCount] = useState<number>(0);
  const [validationResult, setValidationResult] =
    useState<BulkInputFilterValidationResult>();
  const validationDialogState = useBoolean(false);
  const { bulkDialogStates } = useSearchFilterContext();
  const type = useSearchTypeFromRoute();
  const { track } = useTrackEvents();
  const listType = item.metadata?.bulkInput?.listType;
  const { mutateAsync: createSearchList } = useCreateSearchList();
  const searchListIncludeReadApi = useReadSearchList(
    params[fields[0]]?.split(BULK_FILTER_VALUE_SEPARATOR)[0],
    {
      enabled: !!bulkDialogStates[item.field!]?.value,
    }
  );
  const searchListExcludeReadApi = useReadSearchList(
    params[fields[1]]?.split(BULK_FILTER_VALUE_SEPARATOR)[0],
    {
      enabled: !!bulkDialogStates[item.field!]?.value,
    }
  );

  const initialValues: Record<string, string | undefined> = useMemo(() => {
    return {
      [fields[0]]: searchListIncludeReadApi.data?.values.join('\n'),
      [fields[1]]: searchListExcludeReadApi.data?.values.join('\n'),
    };
  }, [
    searchListIncludeReadApi.data?.values,
    searchListExcludeReadApi.data?.values,
    fields,
  ]);

  const config = useMemo(() => {
    return createConfig(item.items ?? [], item);
  }, [item.items]);

  const count = useBulkFilterValuesCount(params[fields[0]], params[fields[1]]);
  // Check if manual search filters (Include/Exclude) are active, excluding own fields
  const hasManualSearchFilters = useMemo(
    () =>
      MANUAL_SEARCH_FILTER_KEYS.filter((key) => !fields.includes(key)).some(
        (key) => manualFilterParams[key]
      ),
    [manualFilterParams, fields]
  );

  const handleSubmit: FormDialogSubmitHandler = (data, state) => {
    state.startProgress();
    const draft = buildBulkInputSearchParamsDraft(data, fields) as Record<
      string,
      string[]
    >;
    if (!listType) return;
    Promise.all([
      draft[fields[0]] &&
        createSearchList({
          type: listType,
          values: draft[fields[0]],
        }),
      draft[fields[1]] &&
        createSearchList({
          type: listType,
          values: draft[fields[1]],
        }),
    ]).then(([includeListPayload, excludeListPayload]) => {
      state.endProgress();
      state.toggle();
      setParams({
        [fields[0]]: includeListPayload
          ? `${includeListPayload.id}${BULK_FILTER_VALUE_SEPARATOR}${includeListPayload.values.length}${BULK_FILTER_VALUE_SEPARATOR}${includeListPayload.description}`
          : undefined,
        [fields[1]]: excludeListPayload
          ? `${excludeListPayload.id}${BULK_FILTER_VALUE_SEPARATOR}${excludeListPayload.values.length}${BULK_FILTER_VALUE_SEPARATOR}${excludeListPayload.description}`
          : undefined,
      });
      if (draft[fields[0]]) {
        track('FILTER', {
          filterName: fields[0],
          filterType: item.type,
          filterValue: draft[fields[0]],
          searchType: type,
        });
      }
      if (draft[fields[1]]) {
        track('FILTER', {
          filterName: fields[1],
          filterType: item.type,
          filterValue: draft[fields[1]],
          searchType: type,
        });
      }
    });
  };

  const handleFormChange = (_isDirty?: boolean, value?: FieldValues) => {
    const validationRes = item.metadata?.validation?.patternValidation?.(
      value || {}
    );
    // Only update state if validation result has actually changed
    if (JSON.stringify(validationRes) !== JSON.stringify(validationResult)) {
      setValidationResult(validationRes);

      const invalidsCount = Object.values(validationRes || {}).reduce(
        (acc, result) => {
          return (
            acc +
            (result.invalids?.length || 0) +
            (result.duplicates?.length || 0)
          );
        },
        0
      );
      setInvalidItemsCount(invalidsCount);
    }
  };

  const handleViewInvalidItems = () => {
    validationDialogState.onTrue();
  };
  return (
    <Box
      display="flex"
      alignItems="center"
      gap={0.5}
      sx={SX.cardContent}
      padding={1}
      className={classnames('BulkInputFilterItem', className)}
    >
      {!!count && (
        <Icon name="check-circle-outline" size={20} color="success.500" />
      )}
      <Box display="flex" flexDirection="column" flex={1}>
        <Typography variant="body4" color="grey.700" fontWeight={500}>
          {item.name}
        </Typography>
        <Typography variant="body5" color="grey.500">
          {`${count} Bulk ${item.metadata?.dialog?.subTitleItemLabel}`}
        </Typography>
      </Box>
      <BulkInputFilterViewButton
        filter={item}
        count={count}
        onClick={() => {
          bulkDialogStates[item.field!].onTrue();
        }}
      />
      {bulkDialogStates[item.field!].value && (
        <FormDialog
          state={bulkDialogStates[item.field!]}
          {...config}
          defaultValues={initialValues}
          submitButtonProps={config.submitButtonProps}
          onSubmit={handleSubmit}
        >
          {item.items?.map((subItem, index) => {
            const { key, ...other } = subItem;
            const invalidItems =
              validationResult?.[subItem.field!]?.invalids ?? [];
            const duplicates =
              validationResult?.[subItem.field!]?.duplicates ?? [];
            return (
              <Fragment key={key}>
                <BulkInputFilterTextarea
                  defaultValue={initialValues[subItem.field!]}
                  key={key}
                  onChange={handleFormChange}
                  invalidItemsCount={invalidItems.length + duplicates.length}
                  {...other}
                />
                <BulkFilterInputError {...item} item={other} />
                {index !== (item.items?.length ?? 0) - 1 && (
                  <Divider sx={SX.divider} />
                )}
              </Fragment>
            );
          })}
          {hasManualSearchFilters && (
            <Alert
              severity="warning"
              icon={<Icon name="warning-amber" size={20} color="warning.700" />}
              sx={SX.alert}
            >
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography
                  variant="body3"
                  color="warning.700"
                  fontWeight={700}
                >
                  Combined Filters are Active
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Typography
                    variant="body3"
                    color="warning.700"
                    fontWeight={400}
                  >
                    You've added companies in Manual Search and Bulk Include /
                    Exclude. Only companies that match both will appear in
                    results.
                  </Typography>
                  <Typography
                    variant="body3"
                    color="warning.700"
                    fontWeight={400}
                  >
                    If you expected more results, remove companies from one of
                    these sections.
                  </Typography>
                </Box>
              </Box>
            </Alert>
          )}
          {invalidItemsCount > 0 && (
            <Alert
              severity="warning"
              icon={<Icon name="warning-amber" size={20} color="grey.500" />}
              action={
                <Button
                  variant="outlined"
                  color="default"
                  size="small"
                  onClick={handleViewInvalidItems}
                >
                  View
                </Button>
              }
              sx={SX.alert}
            >
              <Box display="flex" flexDirection="column" gap={0.5}>
                <Typography variant="body3" color="grey.700" fontWeight={700}>
                  {`Total unrecognized entries (${invalidItemsCount})`}
                </Typography>
                <Typography variant="body3" color="grey.700" fontWeight={400}>
                  A few entries couldn't be recognized. You can see them, but
                  these entries won't Appear in Search Results.
                </Typography>
              </Box>
            </Alert>
          )}
          <BulkFilterValidationsDialog
            state={validationDialogState}
            validationResult={validationResult}
            invalidItemsCount={invalidItemsCount}
            items={item.items}
          />
        </FormDialog>
      )}
    </Box>
  );
});

BulkInputFilterItem.displayName = 'BulkInputFilterItem';
